import DateFnsUtils from "@date-io/date-fns";
import {
  Backdrop,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockIcon from "@mui/icons-material/Block";
import HistoryIcon from "@mui/icons-material/History";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { PuffLoader } from "react-spinners";
import LightTooltip from "../../components/GlobalStyles/LightTooltip";
import StyledCell from "../../components/GlobalStyles/StyledCell";
import StyledTableRow from "../../components/GlobalStyles/StyledTableRow";
import clearIcon from "../../images/buttonIcons/clearIcon.svg";
import { IAdminGetClients } from "../../models/UploadLog/AdminGetClients";
import {
  ExternalAccountSyncStatus,
  IExternalAccountSyncRecord,
  IExternalAccountSyncSearchResult,
} from "../../models/ExternalAccountSync/IExternalAccountSync";
import { useGlobalState } from "../../store/GlobalStore";
import { useFetch, usePost } from "../../utils/apiHelper";
import ApproveRecordDrawer from "./ExternalAccountSyncActions/ApproveRecordDrawer";
import AuditHistoryDrawer from "./ExternalAccountSyncActions/AuditHistoryDrawer";
import RecordDetailDrawer from "./ExternalAccountSyncActions/RecordDetailDrawer";
import RejectRecordDrawer from "./ExternalAccountSyncActions/RejectRecordDrawer";
import {
  getMockClientCodes,
  getMockRecords,
} from "./ExternalAccountSyncMockData";
import Footer from "../../components/Footer/Footer";

const useStyles = makeStyles((theme) => ({
  grid: {
    padding: "3px",
  },
  Title: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  titleHead: {
    marginLeft: "5px",
  },
  topActionsRow: {
    display: "flex",
    justifyContent: "flex-end",
  },
  gridheader: {
    borderRadius: "10px",
    backgroundColor: "white",
    boxShadow: "0px 1px 4px 1px rgba(103, 128, 159, 1)",
  },
  textField: {
    width: 220,
    marginTop: "14px",
  },
  buttons: {
    borderRadius: 20,
    fontSize: "11px",
    background: "#007FFF",
    color: "white",
    paddingLeft: 8,
    marginTop: "10px",
  },
  ClearFilterButtonStyle: {
    borderRadius: 20,
    fontSize: "11px",
    background: "#007FFF",
    color: "white",
    marginLeft: "10px",
    paddingLeft: 8,
    marginTop: "10px",
  },
  IconStyle: {
    height: "15px",
    width: "15px",
    marginLeft: "2px",
  },
  tablebody: {
    maxHeight: "430px",
    minHeight: "430px",
    [theme.breakpoints.up("lg")]: {
      maxHeight: "calc( 100vh - 290px)",
      minHeight: "calc( 100vh - 290px)",
    },
  },
  table: {
    tableLayout: "fixed",
    width: "100%",
  },
  TableCellStyle: {
    background: "#007FFF",
    color: "white",
    fontSize: 12.5,
    fontWeight: 600,
    letterSpacing: 0.3,
    padding: "10px 8px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  TableCellStyle1: {
    fontSize: 13,
    padding: "8px 8px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  bulkToolbar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "6px 14px",
    backgroundColor: "#e3f2fd",
    borderRadius: 20,
  },
  statusChip: {
    fontWeight: 600,
    fontSize: 12,
    height: 24,
  },
  statusChipPending: {
    backgroundColor: "#fff4e5",
    color: "#b26a00",
  },
  statusChipApproved: {
    backgroundColor: "#e3f2fd",
    color: "#0d47a1",
  },
  statusChipProcessed: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
  },
  statusChipRejected: {
    backgroundColor: "#fdecea",
    color: "#b71c1c",
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
  footerBar: {
    marginTop: "10px",
    float: "right",
    marginRight: "70px",
  },
}));

type StatusFilter = "all" | ExternalAccountSyncStatus;

const ExternalAccountSyncDashboard: React.FC = () => {
  const classes = useStyles();
  const { state } = useGlobalState();

  const [records, setRecords] = useState<IExternalAccountSyncRecord[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);
  const [progressBar, setShowProgressBar] = useState(true);
  const [mapped, setMapped] = useState(false);

  // Demo fallback: if the live API errors (e.g. the staging table isn't deployed yet) or
  // returns zero records, the dashboard falls back to a local, realistic mock dataset built
  // from real IHUB sample rows so the full Approve/Reject/bulk workflow can still be
  // demonstrated end-to-end. Once the real API starts returning data, this path is never
  // taken again. See ExternalAccountSyncMockData.ts.
  const [demoMode, setDemoMode] = useState(false);
  const [demoRecords, setDemoRecords] = useState<IExternalAccountSyncRecord[]>(
    [],
  );

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("Pending");
  const [clientCodeFilter, setClientCodeFilter] = useState("");
  const [clientCodeOptions, setClientCodeOptions] = useState<string[]>([]);
  const clientCodesLoading = clientCodeOptions.length === 0;
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const [approveDrawerIds, setApproveDrawerIds] = useState<number[] | null>(
    null,
  );
  const [rejectDrawerIds, setRejectDrawerIds] = useState<number[] | null>(null);
  const [detailStagingId, setDetailStagingId] = useState<number | null>(null);
  const [auditStagingId, setAuditStagingId] = useState<number | null>(null);

  const dateFormat =
    state.GlobalUtils?.settingValue === "DD/MM/YYYY"
      ? "dd/MM/yyyy"
      : state.GlobalUtils?.settingValue === "YYYY/MM/DD"
      ? "yyyy/MM/dd"
      : "MM/dd/yyyy";

  const getFilterPayload = () => ({
    rowsPerPage,
    startingRecordNumber: page * rowsPerPage + 1,
    startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
    endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
    clientCode: clientCodeFilter || null,
    status: statusFilter === "all" ? null : statusFilter,
    searchText: searchText || null,
  });

  const applyFiltersToDemoSet = (source: IExternalAccountSyncRecord[]) => {
    const search = searchText.trim().toLowerCase();
    return source.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (
        clientCodeFilter &&
        r.clientCode.toLowerCase() !== clientCodeFilter.trim().toLowerCase()
      )
        return false;
      if (
        startDate &&
        moment(r.receivedDate).isBefore(moment(startDate), "day")
      )
        return false;
      if (endDate && moment(r.receivedDate).isAfter(moment(endDate), "day"))
        return false;
      if (search) {
        const haystack = [
          r.clientCode,
          r.externalCustomerId,
          r.firstName,
          r.lastName,
          r.businessName,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });
  };

  const loadRecords = async () => {
    setShowProgressBar(true);
    try {
      const response = await usePost<IExternalAccountSyncSearchResult>(
        "ExternalAccountSyncGet",
        getFilterPayload(),
      );
      const apiRecords = response.data?.records || [];
      const apiQty = response.data?.qty || 0;

      if (apiQty > 0) {
        setDemoMode(false);
        setRecords(apiRecords);
        setTotalRecords(apiQty);
        setMapped(false);
        return;
      }

      // API reachable but genuinely empty (fresh deployment) - show the demo dataset.
      enterDemoMode();
    } catch {
      // API not reachable yet (e.g. staging table not deployed) - show the demo dataset.
      enterDemoMode();
    } finally {
      setShowProgressBar(false);
    }
  };

  const enterDemoMode = () => {
    setDemoMode(true);
    const baseline = demoRecords.length > 0 ? demoRecords : getMockRecords();
    if (demoRecords.length === 0) setDemoRecords(baseline);
    if (clientCodeOptions.length === 0)
      setClientCodeOptions(getMockClientCodes());

    const filtered = applyFiltersToDemoSet(baseline);
    const pageStart = page * rowsPerPage;
    setRecords(filtered.slice(pageStart, pageStart + rowsPerPage));
    setTotalRecords(filtered.length);
    setMapped(filtered.length === 0);
  };

  useEffect(() => {
    useFetch<IAdminGetClients[]>("UploadLog/ClientCodeGetPlacement")
      .then((response) => {
        const codes = (response.data || [])
          .map((c) => c.client_code)
          .filter(Boolean);
        if (codes.length > 0) {
          setClientCodeOptions(codes);
        }
      })
      .catch(() => {
        // Client code lookup follows the same demo fallback as the record list.
      });
  }, []);

  useEffect(() => {
    loadRecords();
    setSelectedIds([]);
  }, [
    statusFilter,
    clientCodeFilter,
    searchText,
    startDate,
    endDate,
    page,
    rowsPerPage,
  ]);

  const handleFilter = () => {
    setPage(0);
    loadRecords();
  };

  const handleClearFilter = () => {
    setStatusFilter("Pending");
    setClientCodeFilter("");
    setSearchText("");
    setStartDate(null);
    setEndDate(null);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) =>
    setPage(newPage);
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isPending = (record: IExternalAccountSyncRecord) =>
    record.status === "Pending";

  const getStatusChipClass = (status: ExternalAccountSyncStatus) => {
    if (status === "Rejected") return classes.statusChipRejected;
    if (status === "Processed") return classes.statusChipProcessed;
    if (status === "Approved") return classes.statusChipApproved;
    return classes.statusChipPending;
  };

  const selectablePendingIds = records.filter(isPending).map((r) => r.id);
  const allSelectedOnPage =
    selectablePendingIds.length > 0 &&
    selectablePendingIds.every((id) => selectedIds.includes(id));

  const toggleSelectAll = () => {
    if (allSelectedOnPage) {
      setSelectedIds(
        selectedIds.filter((id) => !selectablePendingIds.includes(id)),
      );
    } else {
      setSelectedIds(
        Array.from(new Set([...selectedIds, ...selectablePendingIds])),
      );
    }
  };

  const toggleSelectOne = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleActionApplied = (
    actionedIds: number[],
    newStatus?: ExternalAccountSyncStatus,
  ) => {
    setRecords((prev) => prev.filter((r) => !actionedIds.includes(r.id)));
    setSelectedIds((prev) => prev.filter((id) => !actionedIds.includes(id)));

    if (demoMode && newStatus) {
      setDemoRecords((prev) =>
        prev.map((r) =>
          actionedIds.includes(r.id)
            ? {
                ...r,
                status: newStatus,
                processedDate:
                  newStatus === "Processed"
                    ? new Date().toISOString()
                    : r.processedDate,
              }
            : r,
        ),
      );
    }
  };

  return (
    <React.Fragment>
      <Backdrop className={classes.backdrop} open={progressBar}>
        <PuffLoader size={80} color={"white"} speedMultiplier={1} />
      </Backdrop>

      <Grid container alignItems="stretch" className={classes.grid}>
        <Grid item xs={12} sm={6} md={7} className={classes.Title}>
          <Typography
            variant="h6"
            gutterBottom
            className={`headertitle ${classes.titleHead}`}
          >
            <b>EXTERNAL ACCOUNT SYNC</b>
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={5} className={classes.topActionsRow}>
          {selectedIds.length > 0 && (
            <Box className={classes.bulkToolbar}>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                {selectedIds.length} selected
              </Typography>
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<CheckCircleOutlineIcon />}
                onClick={() => setApproveDrawerIds(selectedIds)}
              >
                Approve All
              </Button>
              <Button
                size="small"
                variant="contained"
                color="secondary"
                startIcon={<BlockIcon />}
                onClick={() => setRejectDrawerIds(selectedIds)}
              >
                Reject All
              </Button>
            </Box>
          )}
        </Grid>

        <Grid
          container
          spacing={2}
          alignItems="center"
          className={classes.gridheader}
          style={{ margin: 0, width: "100%"}}
        >
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                size="small"
                fullWidth
                label="Start Date"
                variant="inline"
                inputVariant="outlined"
                format={dateFormat}
                disableToolbar
                autoOk
                margin="normal"
                value={startDate}
                onChange={(date) => setStartDate(date)}
                KeyboardButtonProps={{ "aria-label": "change date" }}
                disableFuture
              />
            </MuiPickersUtilsProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                size="small"
                fullWidth
                label="End Date"
                variant="inline"
                inputVariant="outlined"
                format={dateFormat}
                disableToolbar
                autoOk
                margin="normal"
                value={endDate}
                onChange={(date) => setEndDate(date)}
                KeyboardButtonProps={{ "aria-label": "change date" }}
                disableFuture
              />
            </MuiPickersUtilsProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Autocomplete
              size="small"
              fullWidth
              options={clientCodeOptions}
              value={clientCodeFilter || null}
              onChange={(e: any, selected: string | null) =>
                setClientCodeFilter(selected || "")
              }
              loading={clientCodesLoading}
              autoHighlight
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Client Code"
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {clientCodesLoading ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormControl variant="outlined" size="small" fullWidth margin="normal">
              <InputLabel shrink>Status</InputLabel>
              <Select
                label="Status"
                value={statusFilter}
                onChange={(e: any) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Approved">Approved</MenuItem>
                <MenuItem value="Processed">Processed</MenuItem>
                <MenuItem value="Rejected">Rejected</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              label="Search"
              margin="normal"
              placeholder="Name, business, id..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Box display="flex" alignItems="center" style={{ gap: 6 }}>
              <Button
                size="small"
                className={classes.buttons}
                style={{ marginTop: 0 }}
                startIcon={<FilterAltIcon />}
                onClick={handleFilter}
                variant="contained"
                color="primary"
                disabled={!!startDate && !!endDate && startDate > endDate}
              >
                Filter
              </Button>
              <Button
                size="small"
                className={classes.ClearFilterButtonStyle}
                style={{ marginTop: 0, marginLeft: 0 }}
                startIcon={
                  <img
                    src={clearIcon}
                    alt="Icon for clear filter button"
                    className={classes.IconStyle}
                  />
                }
                onClick={handleClearFilter}
                variant="contained"
                color="primary"
              >
                Clear
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <TableContainer
              component={Paper}
              className={`${classes.tablebody} scrollbox on-scrollbar`}
            >
              <Table
                aria-label="external account sync"
                size="small"
                stickyHeader
                className={classes.table}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      className={classes.TableCellStyle}
                      padding="checkbox"
                      style={{ width: 44 }}
                    >
                      <Checkbox
                        size="small"
                        style={{ color: "white" }}
                        checked={allSelectedOnPage}
                        indeterminate={
                          !allSelectedOnPage &&
                          records.some(
                            (r) => isPending(r) && selectedIds.includes(r.id),
                          )
                        }
                        onChange={toggleSelectAll}
                        disabled={selectablePendingIds.length === 0}
                      />
                    </TableCell>
                    <TableCell
                      className={classes.TableCellStyle}
                      style={{ width: "11%" }}
                    >
                      Client Code
                    </TableCell>
                    <TableCell
                      className={classes.TableCellStyle}
                      style={{ width: "15%" }}
                    >
                      External Customer Id
                    </TableCell>
                    <TableCell
                      className={classes.TableCellStyle}
                      style={{ width: "9%" }}
                    >
                      Source
                    </TableCell>
                    <TableCell
                      className={classes.TableCellStyle}
                      style={{ width: "24%" }}
                    >
                      Name / Business
                    </TableCell>
                    <TableCell
                      className={classes.TableCellStyle}
                      style={{ width: "14%" }}
                    >
                      Received Date
                    </TableCell>
                    <TableCell
                      className={classes.TableCellStyle}
                      style={{ width: "11%" }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      className={classes.TableCellStyle}
                      style={{ width: "13%" }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {records.map((record) => (
                    <StyledTableRow
                      key={record.id}
                      hover
                      selected={selectedIds.includes(record.id)}
                    >
                      <StyledCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedIds.includes(record.id)}
                          onChange={() => toggleSelectOne(record.id)}
                          disabled={!isPending(record)}
                        />
                      </StyledCell>
                      <StyledCell className={classes.TableCellStyle1}>
                        {record.clientCode}
                      </StyledCell>
                      <StyledCell className={classes.TableCellStyle1}>
                        {record.externalCustomerId}
                      </StyledCell>
                      <StyledCell className={classes.TableCellStyle1}>
                        <Chip
                          size="small"
                          label={
                            record.recordSource === "NewAccount"
                              ? "New"
                              : "Update"
                          }
                        />
                      </StyledCell>
                      <StyledCell className={classes.TableCellStyle1}>
                        {record.businessName ||
                          `${record.firstName || ""} ${
                            record.lastName || ""
                          }`.trim() ||
                          "—"}
                      </StyledCell>
                      <StyledCell className={classes.TableCellStyle1}>
                        {moment(record.receivedDate).format(
                          state.GlobalUtils?.settingValue,
                        )}{" "}
                        {moment(record.receivedDate).format("HH:mm")}
                      </StyledCell>
                      <StyledCell className={classes.TableCellStyle1}>
                        <Chip
                          size="small"
                          label={record.status}
                          className={`${
                            classes.statusChip
                          } ${getStatusChipClass(record.status)}`}
                        />
                      </StyledCell>
                      <StyledCell
                        className={classes.TableCellStyle1}
                        style={{ whiteSpace: "nowrap" }}
                      >
                        <LightTooltip title="View Detail">
                          <IconButton
                            size="small"
                            onClick={() => setDetailStagingId(record.id)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </LightTooltip>
                        <LightTooltip title="View Audit History">
                          <IconButton
                            size="small"
                            onClick={() => setAuditStagingId(record.id)}
                            style={{ color: "#3949ab" }}
                          >
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                        </LightTooltip>
                        {isPending(record) && (
                          <React.Fragment>
                            <LightTooltip title="Approve">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => setApproveDrawerIds([record.id])}
                              >
                                <CheckCircleOutlineIcon fontSize="small" />
                              </IconButton>
                            </LightTooltip>
                            <LightTooltip title="Reject">
                              <IconButton
                                size="small"
                                onClick={() => setRejectDrawerIds([record.id])}
                                style={{ color: "#b71c1c" }}
                              >
                                <BlockIcon fontSize="small" />
                              </IconButton>
                            </LightTooltip>
                          </React.Fragment>
                        )}
                      </StyledCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
              {mapped ? (
                <Typography
                  variant="h6"
                  gutterBottom
                  style={{ color: "red", marginTop: "10px" }}
                >
                  No records to display..
                </Typography>
              ) : null}
            </TableContainer>
            <Grid container spacing={0}>
              <Grid item xs={12} sm={7}>
                <div className={classes.footerBar}>
                  <Footer />
                </div>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TablePagination
                  rowsPerPageOptions={[15, 25, 50]}
                  component="div"
                  count={totalRecords}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onChangePage={handleChangePage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <ApproveRecordDrawer
        open={!!approveDrawerIds}
        stagingIds={approveDrawerIds || []}
        demoMode={demoMode}
        onClose={() => setApproveDrawerIds(null)}
        onApproved={(ids) => handleActionApplied(ids, "Processed")}
      />
      <RejectRecordDrawer
        open={!!rejectDrawerIds}
        stagingIds={rejectDrawerIds || []}
        demoMode={demoMode}
        onClose={() => setRejectDrawerIds(null)}
        onRejected={(ids) => handleActionApplied(ids, "Rejected")}
      />
      <RecordDetailDrawer
        open={!!detailStagingId}
        stagingId={detailStagingId}
        demoRecord={
          demoMode
            ? demoRecords.find((r) => r.id === detailStagingId) || null
            : null
        }
        onClose={() => setDetailStagingId(null)}
      />
      <AuditHistoryDrawer
        open={!!auditStagingId}
        stagingId={auditStagingId}
        demoRecord={
          demoMode
            ? demoRecords.find((r) => r.id === auditStagingId) || null
            : null
        }
        onClose={() => setAuditStagingId(null)}
      />
    </React.Fragment>
  );
};

export default ExternalAccountSyncDashboard;
